import { AudioManager, SoundEffect } from '../../systems/AudioManager';
import { GameObject } from '../base/GameObject';
import { Earth } from '../earth/Earth';

export abstract class Monster extends GameObject {
  protected speed: number;
  protected reward: number;
  protected specialAbility: string;
  public targetEarth: Earth | null = null;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y);
    this.speed = 20;
    this.reward = 10;
    this.specialAbility = '';
  }

  override update(time: number, delta: number): void {
    // Find Earth if not found
    if (this.targetEarth == null) {
      this.findEarth();
    }
    
    // Move towards Earth
    if (this.targetEarth) {
      this.move();
    }
  }

  setRateSpeed(rate: number): void {
    this.speed = this.speed * rate;
  }

  move(): void {
    if (!this.targetEarth) return;
    
    // Calculate direction to Earth
    const direction = new Phaser.Math.Vector2(
      this.targetEarth.x - this.x,
      this.targetEarth.y - this.y
    ).normalize();
    
    // Move towards Earth
    this.x += direction.x * this.speed * 0.016; // 60 FPS
    this.y += direction.y * this.speed * 0.016;
    
    // Check if reached Earth
    const distance = Phaser.Math.Distance.Between(this.x, this.y, this.targetEarth.x, this.targetEarth.y);
    if (distance < 60) { // Earth radius + monster radius
      this.attackEarth();
    }
  }
  
  findEarth(): Earth | null {
    const earth = this.scene.children.list.find(child =>
      child instanceof Earth
    ) as Earth;

    if (earth) {
      this.targetEarth = earth;
    }
    return earth;
    // Get earth from scene data (set by GamePlayManager)
    // const sceneData = (this.scene as any);
    // const earth = sceneData.earth ?? sceneData.gamePlayManager.gameState.earth;
    
    // if (earth) {
    //   this.targetEarth = earth;
    // }
  }
  // Getters for external access
  getSpeed(): number {
    return this.speed;
  }

  getReward(): number {
    return this.reward;
  }

  getSpecialAbility(): string {
    return this.specialAbility;
  }

  public getIsActive(): boolean {
    return this.isActive;
  }

  attackEarth(): void {
    if (!this.targetEarth) return;
    const _this = this;
    // Create attack impact effect
    this.createEarthAttackEffect();
    
    // Deal damage to Earth (scaled by level)
    const baseDamage = 10;
    if (this.isActive) this.targetEarth.takeDamage(baseDamage);
    
    // Play attack sound
    const audioManager = AudioManager.getInstance(this.scene);
    audioManager.playSFX(SoundEffect.EARTH_DAMAGE);
    
    // Mark as inactive before destroying
    this.isActive = false;
    
    // Destroy monster with explosion effect
    this.createDeathExplosion();
    
    // Small delay before actual destroy for effect
    this.scene?.time.delayedCall(100, () => {
      _this.destroy();
    });
  }
  
  createEarthAttackEffect(): void {
    // Impact flash at contact point
    const impactFlash = this.scene?.add.circle(this.x, this.y, 15, 0xff6600, 0.8);
    this.scene?.tweens.add({
      targets: impactFlash,
      scaleX: 2,
      scaleY: 2,
      alpha: 0,
      duration: 300,
      ease: 'Power2',
      onComplete: () => impactFlash.destroy()
    });
    
    // Damage sparks
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI * 2 * i) / 6;
      const spark = this.scene?.add.circle(this.x, this.y, 2, 0xffaa00);
      
      this.scene?.tweens.add({
        targets: spark,
        x: this.x + Math.cos(angle) * 20,
        y: this.y + Math.sin(angle) * 20,
        alpha: 0,
        duration: 400,
        ease: 'Power2',
        onComplete: () => spark.destroy()
      });
    }
  }

  createDeathExplosion(): void {
    // Monster death explosion
    const explosion = this.scene?.add.circle(this.x, this.y, 8, 0xff4444, 0.9);
    this.scene?.tweens.add({
      targets: explosion,
      scaleX: 2.5,
      scaleY: 2.5,
      alpha: 0,
      duration: 250,
      ease: 'Power2',
      onComplete: () => explosion.destroy()
    });
    
    // Death particles
    for (let i = 0; i < 5; i++) {
      const particle = this.scene?.add.circle(this.x, this.y, 1, 0xff0000);
      const angle = Math.random() * Math.PI * 2;
      const distance = 15 + Math.random() * 10;
      
      this.scene?.tweens.add({
        targets: particle,
        x: this.x + Math.cos(angle) * distance,
        y: this.y + Math.sin(angle) * distance,
        alpha: 0,
        duration: 300,
        delay: i * 20,
        ease: 'Power2',
        onComplete: () => particle.destroy()
      });
    }
  }

  createHeavyAttackEffect(): void {
    // Heavy impact shockwave
    const shockwave = this.scene.add.circle(this.x, this.y, 20, 0xffffff, 0.0);
    shockwave.setStrokeStyle(4, 0xffaa00, 1.0);
    
    this.scene.tweens.add({
      targets: shockwave,
      scaleX: 3,
      scaleY: 3,
      alpha: 0,
      duration: 400,
      ease: 'Power2',
      onComplete: () => shockwave.destroy()
    });
    
    // Heavy impact crater effect
    const crater = this.scene.add.circle(this.x, this.y, 12, 0x444444, 0.6);
    this.scene.tweens.add({
      targets: crater,
      alpha: 0.2,
      duration: 1000,
      onComplete: () => crater.destroy()
    });
    
    // Debris particles
    for (let i = 0; i < 10; i++) {
      const angle = (Math.PI * 2 * i) / 10;
      const debris = this.scene.add.rectangle(this.x, this.y, 2, 2, 0x666666);
      
      this.scene.tweens.add({
        targets: debris,
        x: this.x + Math.cos(angle) * 30,
        y: this.y + Math.sin(angle) * 30 - 15,
        rotation: Math.random() * Math.PI * 2,
        alpha: 0,
        duration: 600,
        ease: 'Power2',
        onComplete: () => debris.destroy()
      });
    }
  }

  createHeavyDeathExplosion(): void {
    // Large orange explosion for tank
    const explosion = this.scene.add.circle(this.x, this.y, 12, 0xff6600, 0.9);
    this.scene.tweens.add({
      targets: explosion,
      scaleX: 3,
      scaleY: 3,
      alpha: 0,
      duration: 400,
      ease: 'Power2',
      onComplete: () => explosion.destroy()
    });
    
    // Secondary explosion
    this.scene.time.delayedCall(100, () => {
      const secondExplosion = this.scene.add.circle(this.x, this.y, 8, 0xffaa00, 0.7);
      this.scene.tweens.add({
        targets: secondExplosion,
        scaleX: 2,
        scaleY: 2,
        alpha: 0,
        duration: 300,
        ease: 'Power2',
        onComplete: () => secondExplosion.destroy()
      });
    });
  }

  abstract activateAbility(): void;
} 